import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SpinnerGapIcon, SealCheckIcon, RocketLaunchIcon, TrophyIcon, PlusIcon, CheckIcon, CopyIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchProvenDevs } from "../lib/api";
import { useTrustedCreators } from "../lib/useTrustedCreators";
import { useSeo } from "../lib/useSeo";

const GOLD = "#F0C000";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

const fmtMcap = (n) => {
  if (!n || n <= 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

const copyAddr = (a) => {
  navigator.clipboard?.writeText(a);
  toast.success("Wallet address copied");
};

const DevRow = ({ d, rank, isAlpha, onAdd }) => {
  const alpha = isAlpha(d.wallet);
  return (
    <div
      data-testid={`proven-dev-row-${d.wallet}`}
      className="relative px-4 sm:px-5 py-4 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: alpha ? `${GOLD}66` : `${GOLD}26`, background: alpha ? `linear-gradient(90deg, ${GOLD}14, transparent)` : rank <= 3 ? `linear-gradient(90deg, ${GOLD}0d, transparent)` : "transparent" }}
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="w-7 text-center font-heading font-black text-lg shrink-0" style={{ color: rank <= 3 ? GOLD : "#666" }}>
          {rank}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid={`proven-dev-copy-${d.wallet}`}
              onClick={() => copyAddr(d.wallet)}
              title="Copy wallet address"
              className="font-mono text-sm font-bold hover:text-neon transition-colors flex items-center gap-1.5"
            >
              {short(d.wallet)} <CopyIcon size={12} className="opacity-50" />
            </button>
            <span className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}88`, color: GOLD }}>
              {d.hitRate}% hit-rate
            </span>
          </div>
          <div className="font-mono text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <span><b className="text-white">{d.launches}</b> launches</span>
            <span>·</span>
            <span><RocketLaunchIcon size={11} weight="bold" className="inline mb-0.5 mr-0.5" style={{ color: GOLD }} /><b style={{ color: GOLD }}>{d.runners}</b> runners</span>
            <span>·</span>
            <span>peak <b className="text-white">{fmtMcap(d.maxPeak)}</b></span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-black text-2xl leading-none" style={{ color: GOLD }}>{d.score}</div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">score</div>
        </div>
        <button
          data-testid={`proven-dev-add-${d.wallet}`}
          onClick={() => onAdd(d)}
          disabled={alpha}
          title={alpha ? "Already on your Alpha Devs" : "Add to Alpha Devs — get pinged on their launches"}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border transition-colors ${
            alpha ? "text-neon border-neon/40 cursor-default" : "text-black hover:opacity-90"
          }`}
          style={alpha ? {} : { background: GOLD, borderColor: GOLD }}
        >
          {alpha ? <><CheckIcon size={14} weight="bold" /> On Alpha</> : <><PlusIcon size={14} weight="bold" /> Add to Alpha</>}
        </button>
      </div>

      {d.best?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mt-3 pl-10">
          {d.best.map((t) => (
            <Link
              key={t.mint}
              to={`/coin/${t.mint}`}
              data-testid={`proven-dev-token-${t.mint}`}
              className="flex items-center gap-1.5 px-2 py-1 border border-white/10 bg-white/[0.02] hover:border-neon/40 transition-colors font-mono text-[11px]"
            >
              <span className="font-bold text-white">${t.symbol || "?"}</span>
              <span className="text-muted-foreground">{fmtMcap(t.peakMcap)}</span>
              {t.mult >= 2 && <span style={{ color: GOLD }}>{t.mult}×</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProvenDevs() {
  const { isAlpha, addWallet } = useTrustedCreators();

  useSeo({
    title: "🏆 Proven Devs — Best Meme Coin Deployers | AlphaApeing",
    description: "A free, data-driven leaderboard of the best token deployers ranked by runners, peak market cap & hit-rate. One-tap add them to your Alpha Devs watch.",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["provenDevs"],
    queryFn: fetchProvenDevs,
    refetchInterval: 120000,
  });
  const devs = data?.devs || [];

  const onAdd = (d) => {
    const name = `Proven ${short(d.wallet)}`;
    addWallet(name, d.wallet);
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="proven-devs-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: GOLD }}>
            <SealCheckIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            PROVEN <span style={{ color: GOLD }}>DEVS</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          A free, data-driven leaderboard of the <span style={{ color: GOLD }} className="font-semibold">best deployers</span> — built from our own
          on-chain launch history, no paid list needed. Ranked by runners, peak market cap & hit-rate.
          One tap adds a dev to your <Link to="/devs" className="text-neon hover:underline">Alpha Devs</Link> so their next launch pings you.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : devs.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <SealCheckIcon size={30} style={{ color: GOLD }} />
          No proven devs yet — the board fills in as we track more deployers and their tokens run.
        </div>
      ) : (
        <div className="border border-white/5 divide-y divide-white/5" data-testid="proven-devs-rows">
          {devs.map((d, i) => <DevRow key={d.wallet} d={d} rank={i + 1} isAlpha={isAlpha} onAdd={onAdd} />)}
        </div>
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <TrophyIcon size={13} style={{ color: GOLD }} />
        Score blends runners (100k+ mcap or 3×+), peak market cap, launch volume & hit-rate. Corrupt upstream values are filtered out. Not financial advice.
      </div>
    </div>
  );
}
